import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_TRAFFIC } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ContentControlTrafficExclusion, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ContentControlTrafficFormValue,
  ContentControlTrafficSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.types';
import {
  CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE,
  GENERAL_ADVANCED_SETTINGS_MITM_DEFAULT_VALUE,
  GENERAL_ADVANCED_SETTINGS_SCAN_CLIENT_HELLO_DEFAULT_VALUE,
  GENERAL_ADVANCED_SETTINGS_SCAN_SSL_DEFAULT_VALUE,
  getContentControlTrafficSearchableKeywords,
} from '../../../../utils/uiSettings/contentControl/content-control.values';
import {
  NetworkProtectionApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { Feature } from '@nirvana/shared/constants';
import { classMap } from 'lit-html/directives/class-map.js';
import { ButtonColor } from '../../../../../../../ui/gzIconButton/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GeneralAdvancedScanClientHelloBlockType } from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../ui/gzChipsGeneratorInput';
import { GzContentControlTrafficExclusionsGridWebComponent } from '../gridComponents/GzContentControlTrafficExclusionsGridWebComponent';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { GzDialogWebComponent } from '../../../../../../../ui/gzDialog';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_TRAFFIC)
export class GzContentControlTrafficWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ContentControlTrafficFormValue,
  ContentControlTrafficSearchableKeywords,
  NetworkProtectionApiClients
> {
  protected path = {
    moduleId: ModuleId.CONTENT_CONTROL,
    sectionId: SectionId.CONTENT_CONTROL_TRAFFIC,
  };
  protected searchableKeywords = getContentControlTrafficSearchableKeywords();

  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);

  @query('#content-control-traffic-enable')
  private trafficEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#content-control-traffic-content')
  private trafficContent: HTMLElement;
  @query('#content-control-traffic-info-text')
  private trafficInfoText: GzTextWebComponent;

  // general settings
  @query('#content-control-traffic-scan-ssl-enable-form-field')
  private scanSslEnableFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-ssl-content')
  private scanSslContent: HTMLElement;
  @query('#content-control-traffic-scan-ssl-http-form-field')
  private scanSslHttpFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-ssl-ftp-form-field')
  private scanSslFtpFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-ssl-ssh-form-field')
  private scanSslSshFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-imap-form-field')
  private scanImapFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-mapi-form-field')
  private scanMapiFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-pop3-form-field')
  private scanPop3FormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-smtp-form-field')
  private scanSmtpFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-exclude-finance-form-field')
  private excludeFinanceFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-ssl-mitm-enabled-form-field')
  private scanSslMitmEnabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-ssl-mitm-processes-form-field')
  private scanSslMitmProcessesFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#content-control-traffic-scan-client-enable-form-field')
  private scanClientEnableFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-scan-client-block-type-form-field')
  private scanClientBlockTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#content-control-traffic-show-browser-toolbar-form-field')
  private showBrowserToolbarFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#content-control-traffic-browser-search-advisor-form-field')
  private browserSearchAdvisorFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // exclusions settings
  @query('#content-control-traffic-enable-exclusions')
  private trafficEnableExclusionsField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#exclusions-items-form-field')
  private exclusionsItemsFormField: GzFormFieldWebComponent<
    GzContentControlTrafficExclusionsGridWebComponent,
    ContentControlTrafficExclusion[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="content-control-traffic-enable" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.NetworkProtection].label}"
        gz-form-input
        @toggleChange="${e => this.toggleModuleStatus(!e.detail.checked)}"
      >
        <span
          >${this.searchableKeywords[ContentControlTrafficSearchableKeywords.NetworkProtection].translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText" id="content-control-traffic-info-text"
      >${translateFn(
        'Configure your preferences for content filtering, data protection, web access, application usage, traffic scanning, antiphishing, and network attack detection. Disabling this option will deactivate all network protection features and their settings.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const classes = classMap({
      hidden: this.isCompanionStandalone,
    });

    return html`
      <div id="content-control-traffic-content">
        <div class="${classes}">
          <gz-title
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.GeneralSettings].label}"
            >${this.searchableKeywords[ContentControlTrafficSearchableKeywords.GeneralSettings]
              .translatedLabel}</gz-title
          >

          <gz-form-field class="label-mw-250 checkbox-width" id="content-control-traffic-scan-ssl-enable-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[
                ContentControlTrafficSearchableKeywords.InterceptEncryptedTraffic
              ].label}"
              label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.InterceptEncryptedTraffic]
                .translatedLabel}"
              gz-form-input
              @change="${this.onSslEnableChange.bind(this)}"
            ></gz-checkbox>
          </gz-form-field>

          <div id="content-control-traffic-scan-ssl-content">
            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-ssl-http-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanHttps]
                      .label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanHttps]
                      .translatedLabel}"
                    gz-form-input
                    @change="${this.onSslHttpEnableChange.bind(this)}"
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-MacOS" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <gz-form-field class="label-mw-250" id="content-control-traffic-scan-ssl-mitm-enabled-form-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.Additional].label}"
                label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.Additional].translatedLabel}"
                gz-form-input
                @change="${this.onSslMitmEnableChange.bind(this)}"
              >
              </gz-checkbox>
            </gz-form-field>

            <gz-form-field class="ml-6" id="content-control-traffic-scan-ssl-mitm-processes-form-field">
              <gz-chips-generator-input
                gz-form-input
                themeInput
                .strings="${{
                  placeholder: translateFn('Process name'),
                  notUniqueErrorMessage: translateFn('The entry already exists.'),
                  hintText: translateFn('Use a semicolon (;) to separate multiple processes.'),
                }}"
              ></gz-chips-generator-input>
            </gz-form-field>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-ssl-ftp-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanFtps]
                      .label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanFtps].translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-ssl-ssh-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanSsh].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanSsh].translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-linux" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-imap-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanImaps].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanImaps].translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-mapi-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanMapi].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanMapi].translatedLabel}"
                    @change="${this.onScanMapiChanged.bind(this)}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <gz-text
              fontSize="12"
              fontWeight="400"
              class="ml-3 mb-2 infoText"
              id="content-control-traffic-scan-mapi-text"
            >
              ${translateFn(
                'MAPI email scanning is only available for Microsoft 365 domains. Verify compatibility before enabling.'
              )}
            </gz-text>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-pop3-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanPop3].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanPop3].translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-scan-smtp-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanSmtp].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ScanSmtp].translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <div class="field-with-icon-row">
              <div>
                <gz-form-field class="label-mw-250" id="content-control-traffic-exclude-finance-form-field">
                  <gz-checkbox
                    searchable-label="${this.searchableKeywords[
                      ContentControlTrafficSearchableKeywords.ExcludeFinanceDomains
                    ].label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ExcludeFinanceDomains]
                      .translatedLabel}"
                    gz-form-input
                  >
                  </gz-checkbox>
                </gz-form-field>
              </div>
              <div>
                <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                <gz-icon name="ds-icon-MacOS" color="${ButtonColor.SECONDARY}"></gz-icon>
              </div>
            </div>

            <gz-info-box
              id="content-control-traffic-scan-text"
              severity="warning"
              class="mb-3"
            >
              ${translateFn('Enabling Extended Protection on mail servers may lead to compatibility issues with certain email protocol scanning features. The following protocols may be affected: IMAPS, MAPI, POP3S, and SMTPS.')}
            </gz-info-box>
          </div>

          <gz-form-field class="label-mw-250 checkbox-width" id="content-control-traffic-scan-client-enable-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.InterceptTls].label}"
              label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.InterceptTls].translatedLabel}"
              gz-form-input
              @change="${e => toggleDisabled(this.scanClientBlockTypeFormField, !e.detail.checked)}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field id="content-control-traffic-scan-client-block-type-form-field" class="ml-3">
            <gz-radio-group
              gz-form-input
              .selectedOption="${GeneralAdvancedScanClientHelloBlockType.INSERT_ERROR_PAGE}"
            >
              <div class="field-with-icon-row">
                <div>
                  <gz-radio
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.AccessDenied]
                      .label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.AccessDenied]
                      .translatedLabel}"
                    .value="${GeneralAdvancedScanClientHelloBlockType.INSERT_ERROR_PAGE}"
                  ></gz-radio>
                </div>
                <div>
                  <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                </div>
              </div>
              <div class="field-with-icon-row">
                <div>
                  <gz-radio
                    searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ResetConnection]
                      .label}"
                    label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.ResetConnection]
                      .translatedLabel}"
                    .value="${GeneralAdvancedScanClientHelloBlockType.RESET_CONNECTION}"
                  ></gz-radio>
                </div>
                <div>
                  <gz-icon name="ds-icon-windows" color="${ButtonColor.SECONDARY}"></gz-icon>
                </div>
              </div>
            </gz-radio-group>
          </gz-form-field>

          <gz-form-field
            class="label-mw-250 checkbox-width hidden"
            id="content-control-traffic-show-browser-toolbar-form-field"
          >
            <gz-checkbox gz-form-input></gz-checkbox>
          </gz-form-field>
          <gz-form-field
            class="label-mw-250 checkbox-width hidden"
            id="content-control-traffic-browser-search-advisor-form-field"
          >
            <gz-checkbox gz-form-input></gz-checkbox>
          </gz-form-field>
        </div>

        <div>
          <gz-form-field id="content-control-traffic-enable-exclusions" class="mbw-1 mt-4">
            <gz-toggle
              gz-form-input
              @toggleChange="${e => toggleVisibility(this.exclusionsItemsFormField, e.detail.checked)}"
            >
              <gz-title
                fontSize="11"
                fontWeight="600"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[ContentControlTrafficSearchableKeywords.Exclusions].label}"
                >${this.searchableKeywords[ContentControlTrafficSearchableKeywords.Exclusions]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <div id="content-control-traffic-exclusions-content">
            <gz-text id="content-control-traffic-exclusions-text" fontSize="12" fontWeight="400" class="infoText"
              >${translateFn('Configure exclusions to skip specific types of traffic during scanning.')}
            </gz-text>

            <gz-form-field id="exclusions-items-form-field">
              <gz-policy-panel-content-control-traffic-exclusions-grid
                class="removeGridPadding addPaddingGridTop"
                gz-form-input
              ></gz-policy-panel-content-control-traffic-exclusions-grid>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Set clients.
   */
  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'NetworkProtection'
    ) as NetworkProtectionApiClients;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.contentControl.traffic
   */
  protected getValueFromManager(): ContentControlTrafficFormValue {
    // get default value
    const defaultValue = CONTENT_CONTROL_TRAFFIC_DEFAULT_VALUE;
    const policyManagerInstance = PolicyPanelManager.getInstance();

    // get value from manager
    const value = {
      uiSettings: {
        general: {
          display: {
            silentMode: {
              showBrowserToolbar: policyManagerInstance.getPolicy(
                'uiSettings.general.display.silentMode.showBrowserToolbar',
                false
              ),
            },
          },
          advanced: {
            settings: {
              scanSsl: policyManagerInstance.getPolicy(
                'uiSettings.general.advanced.settings.scanSsl',
                GENERAL_ADVANCED_SETTINGS_SCAN_SSL_DEFAULT_VALUE
              ),
              excludeFinanceDomains: policyManagerInstance.getPolicy(
                'uiSettings.general.advanced.settings.excludeFinanceDomains',
                false
              ),
              mitm: policyManagerInstance.getPolicy(
                'uiSettings.general.advanced.settings.mitm',
                GENERAL_ADVANCED_SETTINGS_MITM_DEFAULT_VALUE
              ),
              scanClientHello: policyManagerInstance.getPolicy(
                'uiSettings.general.advanced.settings.scanClientHello',
                GENERAL_ADVANCED_SETTINGS_SCAN_CLIENT_HELLO_DEFAULT_VALUE
              ),
              browserSearchAdvisor: policyManagerInstance.getPolicy(
                'uiSettings.general.advanced.settings.browserSearchAdvisor',
                true
              ),
            },
          },
        },
        [ModuleId.CONTENT_CONTROL]: {
          [SectionId.CONTENT_CONTROL_TRAFFIC]: {
            enable: policyManagerInstance.getPolicy('uiSettings.contentControl.traffic.enable', true),
            enableExclusions: policyManagerInstance.getPolicy(
              'uiSettings.contentControl.traffic.enableExclusions',
              false
            ),
            exclusions: policyManagerInstance.getPolicy('uiSettings.contentControl.traffic.exclusions', []),
          },
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          display: {
            silentMode: {
              showBrowserToolbar: this.showBrowserToolbarFormField,
            },
          },
          advanced: {
            settings: {
              scanSsl: {
                enabled: this.scanSslEnableFormField,
                protocols: {
                  outgoing: {
                    http: this.scanSslHttpFormField,
                    ftp: this.scanSslFtpFormField,
                    ssh: this.scanSslSshFormField,
                    imap: this.scanImapFormField,
                    mapi: this.scanMapiFormField,
                    pop3: this.scanPop3FormField,
                    smtp: this.scanSmtpFormField,
                  },
                },
              },
              excludeFinanceDomains: this.excludeFinanceFormField,
              mitm: {
                enabled: this.scanSslMitmEnabledFormField,
                processes: this.scanSslMitmProcessesFormField,
              },
              scanClientHello: {
                enabled: this.scanClientEnableFormField,
                blockType: this.scanClientBlockTypeFormField,
              },
              browserSearchAdvisor: this.browserSearchAdvisorFormField,
            },
          },
        },
        contentControl: {
          traffic: {
            enable: this.trafficEnableField,
            enableExclusions: this.trafficEnableExclusionsField,
            exclusions: this.exclusionsItemsFormField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        general: {
          display: {
            silentMode: {
              showBrowserToolbar: this.valueT.uiSettings.general.display.silentMode.showBrowserToolbar,
            },
          },
          advanced: {
            settings: {
              scanSsl: this.valueT.uiSettings.general.advanced.settings.scanSsl,
              excludeFinanceDomains: this.valueT.uiSettings.general.advanced.settings.excludeFinanceDomains,
              mitm: this.valueT.uiSettings.general.advanced.settings.mitm,
              scanClientHello: this.valueT.uiSettings.general.advanced.settings.scanClientHello,
              browserSearchAdvisor: this.valueT.uiSettings.general.advanced.settings.browserSearchAdvisor,
            },
          },
        },
        contentControl: {
          traffic: {
            enable: this.valueT.uiSettings.contentControl.traffic.enable,
            enableExclusions: this.valueT.uiSettings.contentControl.traffic.enableExclusions,
            exclusions: this.valueT.uiSettings.contentControl.traffic.exclusions,
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings'], {}) as FormGroupValue;
    }

    return value;
  }

  /**
   * ExtJs useful method. It connects Firewall module with the current section.
   */
  public addExclusionItem(item: ContentControlTrafficExclusion) {
    this.exclusionsItemsFormField.Input.addExclusionItem(item);
  }

  /**
   * Enable/Disable module.
   * @param disabled
   */
  toggleModuleStatus(disabled: boolean) {
    toggleDisabled(this.trafficContent, disabled);
    toggleDisabled(this.trafficInfoText, disabled);

    PolicyPanelManager.getInstance().setContentControlSectionsMask(disabled);
  }

  onSslEnableChange(event: CustomEvent) {
    const value = event.detail.checked;
    if (value) {
      this.onInterceptEncryptedTrafficEnable();
    } else {

      const policyPanelManager = PolicyPanelManager.getInstance();
      const isNetworkAttackDefenseEnabled = policyPanelManager.getPolicy("uiSettings.contentControl.networkMonitor.enable", false);
      const isRdpTrafficScanningEnabled = policyPanelManager.getPolicy("uiSettings.contentControl.networkMonitor.inspectRdpTraffic", false);

      if (this.isReady && isNetworkAttackDefenseEnabled && isRdpTrafficScanningEnabled) {

        const dialogFooterHtml = `
          <gz-button id='confirm' type='outline' color='negative' class='mr-2'>
           ${translateFn('Disable')}
          </gz-button>
          <gz-button id='cancel' type='outline' color='secondary'>
            ${translateFn('Cancel')}
          </gz-button>
        `;

        GzDialogWebComponent.computeCustomDialog(
          translateFn("Disabling this option also disables RDP traffic inspection in Network Protection > Network Attacks."),
          dialogFooterHtml,
          translateFn("Disable encrypted traffic interception?"),
          () => { // cancel action
            this.scanSslEnableFormField.value = true;
          },
          () => { // confirm action
            PolicyPanelManager.getInstance().setPartialPolicy({
              uiSettings: {
                contentControl: {
                  networkMonitor: {
                    inspectRdpTraffic: false
                  }
                }
              }
            });
          },
        );

      } else {
        this.onInterceptEncryptedTrafficDisable();
      }

    }

    if (!value) {
      this.scanSslHttpFormField.value = false;
      this.scanSslFtpFormField.value = false;
      this.scanSslSshFormField.value = false;
      this.scanImapFormField.value = false;
      this.scanMapiFormField.value = false;
      this.scanPop3FormField.value = false;
      this.scanSmtpFormField.value = false;
      this.excludeFinanceFormField.value = false;
    }

    toggleDisabled(this.scanSslContent, !value);
  }

  onInterceptEncryptedTrafficDisable() {
    this.scanSslHttpFormField.value = false;
    this.scanSslFtpFormField.value = false;
    this.scanSslSshFormField.value = false;
    this.excludeFinanceFormField.value = false;
    toggleDisabled(this.scanSslContent, false);
  }

  onInterceptEncryptedTrafficEnable() {
    toggleDisabled(this.scanSslContent, false);
  }

  onSslHttpEnableChange(event: CustomEvent) {
    const value = event.detail.checked;

    if (!value) {
      this.scanSslMitmEnabledFormField.value = false;
    }

    toggleDisabled(this.scanSslMitmEnabledFormField, !value);
  }

  onSslMitmEnableChange(event: CustomEvent) {
    const value = event.detail.checked;

    if (!value) {
      this.scanSslMitmProcessesFormField.value = [];
    }

    toggleVisibility(this.scanSslMitmProcessesFormField, value);
  }

  onScanMapiChanged(event: CustomEvent) {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    const value = event.detail.checked;
    if (value) {
      return ;
    }

    // uncheck mapi checkbox in Network Protection > Web Protection > Email Traffic Scan
    PolicyPanelManager.getInstance().setNetworkProtectionEmailTrafficScan(
      ['incomingAndOutgoingEmails', 'mapi'],
      value
    );

    // uncheck ScanSsl, if all fields are unchecked
    const fieldsToCheck = ['incomingEmails', 'outgoingEmails', 'incomingAndOutgoingEmails'];
    const trafficScan = PolicyPanelManager.getInstance().getPolicy('uiSettings.contentControl.traffic.trafficScan', {});
    const allFalse = fieldsToCheck.every(field =>
      Object.values(trafficScan[field]).every(subfield => !subfield)
    );

    if (allFalse) {
      PolicyPanelManager.getInstance().setNetworkProtectionEmailTrafficScan(
        ['emailTraffic'],
        value
      );
    }
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.exclusionsItemsFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_TRAFFIC: GzContentControlTrafficWebComponent;
  }
}
